import { Suspense, lazy, useRef, useState, useEffect } from "react";
import HeroText from "../components/HeroText";
import ParallaxBackground from "../components/ParallaxBackground";
import ErrorBoundary from "../components/ErrorBoundary";
import { useWebGLCapability } from "../hooks/useWebGLCapability";
import { useIntersectionLoad } from "../hooks/useIntersectionLoad";

const HeroScene = lazy(() => import("../components/HeroScene"));

const HeroSceneFallback = () => (
  <div
    className="hero-scene__fallback absolute inset-0 bg-gradient-to-b from-black/80 to-black/60"
    aria-hidden="true"
  />
);

/**
 * Hero section with lazy-loaded 3D scene.
 * Renders based on device capabilities and viewport visibility.
 * Uses IntersectionObserver and requestIdleCallback for optimal loading timing.
 */
const Hero = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const canRenderWebGL = useWebGLCapability();
  const shouldLoadScene = useIntersectionLoad(sectionRef, {
    rootMargin: "150px",
    timeout: 3000,
  });

  const [deferLoad, setDeferLoad] = useState(false);

  useEffect(() => {
    // Defer 3D load by 2 seconds to allow LCP/FCP to settle
    const t = setTimeout(() => setDeferLoad(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const showScene = canRenderWebGL && shouldLoadScene && deferLoad;

  return (
    <section
      id="home"
      className="relative flex w-full justify-center min-h-[100dvh] overflow-hidden md:items-start md:justify-start c-space"
      ref={sectionRef}
    >
      <HeroText />
      <ParallaxBackground />
      <figure className="absolute inset-0 h-full w-full pointer-events-none">

        <ErrorBoundary fallback={<HeroSceneFallback />}>
          {showScene ? (
            <Suspense fallback={<HeroSceneFallback />}>
              <div className="w-full h-full pointer-events-auto">
                <HeroScene />
              </div>
            </Suspense>
          ) : (
            <HeroSceneFallback />
          )}
        </ErrorBoundary>

      </figure>
    </section>
  );
};

export default Hero;
